import React from 'react';
import GroupControl from '../ui/GroupControl';
import { FLAT_SORTS } from '../../constants/gagBreakdown';

/**
 * The five group-by modes, ordered for the menu: **the flat (no-groups) mode
 * FIRST**, then the groupings.
 *
 * ⚖️ **FLAT MOVED TO THE TOP ON 2026-09-22 — owner ruling, and it is the same
 * decision as the page's new default, expressed in a second place.** It was
 * LAST, under a comment reading *"the primary default first … then the flat
 * (no-groups) mode last"* — which was a true description of a page that
 * defaulted to `sub_department`. `GagBreakdown.js` now seeds `groupBy` from the
 * literal `'all'`, so leaving flat at the bottom would open the menu with its
 * FIFTH row ticked and the first row looking like the default. **Keep the two
 * in step: whichever mode the page opens on leads this list.**
 *
 * 🔑 **THIS IS `GagGroupControl`'S OWN DISPLAY ARRAY AND IS NOT
 * `constants/gagBreakdown.js#GROUP_MODES`.** That export is a VALIDATION
 * whitelist (the page's stored-value guard reads it) and carries no labels and
 * no display order — reordering it changes nothing on screen. The duplication
 * is deliberate: one array answers *"is this a legal value"*, this one answers
 * *"what does the user see, and in what order"*. 🪤 Do not "unify" them; a
 * whitelist that has to stay in display order is a whitelist you cannot
 * extend without redesigning a menu.
 *
 * 📝 **The LABEL text is untouched.** The owner named the TIMELINE control's
 * label, not this one; renaming `Flat list (no groups)` here was not asked for.
 *
 * Ids are otherwise UNCHANGED — this control is a nicer shell over the same
 * `groupBy` setter the page already owns. 🔻 **"persisted state" used to be
 * part of that sentence and is now FALSE: the page persists NONE of this since
 * 2026-09-22** (owner ruling — it opens on its defaults every time). Pinned by
 * `GagGroupControl.optionOrder.test.js`, whose §2 asserts that a row still
 * dispatches its OWN id from its new position (position and id are separate
 * facts, and a reorder is exactly the edit that desyncs them).
 */
const GROUP_OPTIONS = [
  { id: 'all', label: 'Flat list (no groups)' },
  { id: 'sub_department', label: 'Sub-department' },
  { id: 'shoot_week', label: 'Shoot date' },
  { id: 'scene', label: 'Scene' },
  { id: 'assignment', label: 'Assignment status' },
];

/**
 * Group ▾ for the Breakdown — a THIN WRAPPER over the shared `ui/GroupControl`
 * since 2026-10-02 (Gags/teams/tasks Stage 1 reuse plan: one group control for
 * the Breakdown and My Tasks). It passes the Breakdown's own `GROUP_OPTIONS`
 * (above) and its flat sort (`FLAT_SORTS`); every rule about the control
 * itself — the ghost trigger, the sort section shown always in flat mode and
 * below `md` in grouped modes, nothing persisted — lives in `ui/GroupControl`.
 *
 * Props (all wired to the page's existing state/handlers): groupBy,
 * onChangeGroupBy, flatSort, flatSortDir, onChangeSort(col) (toggles dir on
 * re-pick), onToggleSortDir(). SESSION state since 2026-09-22 (owner ruling);
 * do not reintroduce a stored grouping.
 */
export default function GagGroupControl({
  groupBy,
  onChangeGroupBy,
  flatSort,
  flatSortDir,
  onChangeSort,
  onToggleSortDir,
  // 📱 Phone row 2 (2026-10-03 prototype): OPTIONAL passthroughs to
  // ui/GroupControl's opt-in props. All omitted on desktop, so the desktop
  // trigger and panel are byte-identical.
  renderLabel,
  heading,
  closeOnSelect,
  shrinkable,
  testId,
}) {
  return (
    <GroupControl
      options={GROUP_OPTIONS}
      value={groupBy}
      onChange={onChangeGroupBy}
      flatId="all"
      renderLabel={renderLabel}
      {...(heading ? { heading } : {})}
      closeOnSelect={closeOnSelect}
      shrinkable={shrinkable}
      testId={testId}
      fallbackLabel="Sub-department"
      // Grouped modes hide the sort section at `md` and up, so don't advertise
      // sort there — only promise it where the menu offers it at any width.
      title="Group the table — and choose the sort"
      titleGrouped="Group the table"
      sort={{
        options: FLAT_SORTS,
        field: flatSort,
        dir: flatSortDir,
        onChangeField: onChangeSort,
        onToggleDir: onToggleSortDir,
      }}
    />
  );
}
